import React, {useEffect, useMemo, useState} from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Spin,
  Switch,
  message,
} from 'antd';
import PageContainer from '../../components/common/PageContainer';
import {useTranslation} from '../../hooks/useTranslation';
import {useAppSelector} from '../../hooks/hooks';
import {selectUser} from '../../store/slices/SessionSlice';
import {
  EmailServerConfig,
  useEmailServerConfig,
  useSaveEmailServerConfig,
  useTestEmailServerConfig,
} from '../../hooks/settings/useEmailServer';
import './styles/email-server.less';
import {extractErrorMessage} from '../../utils/apiError';
import {EMAIL_PATTERN, isValidEmail} from '../../utils/validation';

const KEYS = 'settings.content.emailServer';

interface EmailServerFormValues {
  from: string;
  fromName: string;
  replyTo: string;
  replyToName: string;
  envelopeFrom: string;
  host: string;
  port: string;
  username: string;
  password: string;
  ssl: boolean;
  starttls: boolean;
  auth: boolean;
  debug: boolean;
}

const INITIAL_VALUES: EmailServerFormValues = {
  from: '',
  fromName: '',
  replyTo: '',
  replyToName: '',
  envelopeFrom: '',
  host: '',
  port: '',
  username: '',
  password: '',
  ssl: false,
  starttls: false,
  auth: false,
  debug: false,
};

const toFormValues = (config: EmailServerConfig): EmailServerFormValues => ({
  from: config.from ?? '',
  fromName: config.fromName ?? '',
  replyTo: config.replyTo ?? '',
  replyToName: config.replyToName ?? '',
  envelopeFrom: config.envelopeFrom ?? '',
  host: config.host ?? '',
  port: config.port ?? '',
  username: config.username ?? '',
  password: '',
  ssl: config.ssl,
  starttls: config.starttls,
  auth: config.auth,
  debug: config.debug,
});

const toPayload = (values: EmailServerFormValues): EmailServerConfig => ({
  ...values,
  password: values.password.trim() ? values.password : undefined,
});

const EmailServer: React.FC = () => {
  const {t} = useTranslation();
  const [form] = Form.useForm<EmailServerFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const user = useAppSelector(selectUser);
  const userEmail = user?.email || '';

  const [saved, setSaved] = useState<EmailServerFormValues>(INITIAL_VALUES);
  const [passwordSet, setPasswordSet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [verified, setVerified] = useState(false);
  const [notice, setNotice] = useState('');

  const {data: config, isLoading, isError} = useEmailServerConfig();
  const saveConfig = useSaveEmailServerConfig();
  const testConfig = useTestEmailServerConfig();

  useEffect(() => {
    if (!config) return;
    const loaded = toFormValues(config);
    setSaved(loaded);
    form.setFieldsValue(loaded);
    setPasswordSet(!!config.passwordSet);
  }, [config, form]);

  const watched = Form.useWatch([], form);
  const values: EmailServerFormValues = useMemo(
    () => ({...saved, ...(watched ?? {})}),
    [saved, watched],
  );

  const dirty = useMemo(
    () =>
      (Object.keys(INITIAL_VALUES) as (keyof EmailServerFormValues)[]).some(
        (key) => values[key] !== saved[key],
      ),
    [values, saved],
  );
  const valid = isValidEmail(values.from) && values.host.trim().length > 0;
  const suggestedPort = values.ssl ? '465' : values.starttls ? '587' : '25';
  const hostLabel = values.host.trim() || t(`${KEYS}.defaultHostLabel`);

  const handleValuesChange = (changed: Partial<EmailServerFormValues>) => {
    setNotice('');
    setVerified(false);
    // SSL and StartTLS are mutually exclusive (per design): enabling one clears the other.
    if (changed.ssl) form.setFieldsValue({starttls: false});
    if (changed.starttls) form.setFieldsValue({ssl: false});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const current: EmailServerFormValues = form.getFieldsValue(true);
      const result = await saveConfig.mutateAsync(toPayload(current));
      const persisted = {...current, password: ''};
      setSaved(persisted);
      form.setFieldsValue({password: ''});
      setPasswordSet(result?.passwordSet ?? (passwordSet || !!current.password.trim()));
      setNotice(t(`${KEYS}.messages.saved`));
    } catch (error) {
      void messageApi.error(extractErrorMessage(error, t(`${KEYS}.messages.saveError`)));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setNotice('');
    try {

      await testConfig.mutateAsync(toPayload(form.getFieldsValue(true)));
      setVerified(true);
      setNotice(t(`${KEYS}.messages.testSuccess`).replace('{email}', userEmail));
    } catch (error) {
      void messageApi.error(extractErrorMessage(error, t(`${KEYS}.messages.testError`)));
    } finally {
      setTesting(false);
    }
  };

  const handleRevert = () => {
    form.setFieldsValue(saved);
    setNotice('');
    setVerified(false);
  };

  const sectionHead = (section: 'template' | 'connection') => (
    <div className="gt-mail-section-head">
      <span className="gt-mail-section-title">{t(`${KEYS}.sections.${section}.title`)}</span>
      <span className="gt-mail-section-desc">
        {t(`${KEYS}.sections.${section}.description`)}
      </span>
    </div>
  );

  const statusIndicator = (
    <span className="gt-mail-status">
      <span className={`gt-mail-dot gt-mail-dot--${verified ? 'on' : 'off'}`}/>
      <span className="gt-mail-status-text">
        {t(`${KEYS}.status.${verified ? 'verified' : 'notVerified'}`)}
      </span>
    </span>
  );

  // The test hint embeds the current user e-mail — render it in the mono face.
  const [testInfoBefore, testInfoAfter] = t(`${KEYS}.testInfo`).split('{email}');

  return (
    <PageContainer
      title={t(`${KEYS}.title`)}
      subTitle={t(`${KEYS}.description`)}
      tags={statusIndicator}
      className="gt-py-x8 gt-px-x12"
      titleClassName="h3 gt-text-color-neutral-800"
    >
      {contextHolder}
      <div className="gt-mail">
        {notice && <Alert type="success" showIcon title={notice}/>}
        {isError && (
          <Alert type="error" showIcon title={t(`${KEYS}.messages.loadError`)}/>
        )}

        <Spin spinning={isLoading}>
          <Form
            form={form}
            className="gt-mail-form"
            layout="horizontal"
            labelAlign="left"
            colon={false}
            initialValues={INITIAL_VALUES}
            onFinish={handleSave}
            onValuesChange={handleValuesChange}
            requiredMark
          >
            <Card className="gt-mail-section" title={sectionHead('template')}>
              <div className="gt-mail-section-body">
                <div className="gt-mail-rows">
                  <Form.Item
                    className="gt-mail-field"
                    name="from"
                    label={t(`${KEYS}.fields.from.label`)}
                    rules={[
                      {required: true, message: t(`${KEYS}.messages.required`)},
                      {pattern: EMAIL_PATTERN, message: t(`${KEYS}.messages.invalidEmail`)},
                    ]}
                  >
                    <Input placeholder={t(`${KEYS}.fields.from.placeholder`)}/>
                  </Form.Item>
                  <Form.Item
                    className="gt-mail-field"
                    name="fromName"
                    label={t(`${KEYS}.fields.fromName.label`)}
                    tooltip={t(`${KEYS}.fields.fromName.tooltip`)}
                  >
                    <Input placeholder={t(`${KEYS}.fields.fromName.placeholder`)}/>
                  </Form.Item>
                  <Form.Item
                    className="gt-mail-field"
                    name="replyTo"
                    label={t(`${KEYS}.fields.replyTo.label`)}
                    rules={[
                      {pattern: EMAIL_PATTERN, message: t(`${KEYS}.messages.invalidEmail`)},
                    ]}
                  >
                    <Input placeholder={t(`${KEYS}.fields.replyTo.placeholder`)}/>
                  </Form.Item>
                  <Form.Item
                    className="gt-mail-field"
                    name="replyToName"
                    label={t(`${KEYS}.fields.replyToName.label`)}
                    tooltip={t(`${KEYS}.fields.replyToName.tooltip`)}
                  >
                    <Input placeholder={t(`${KEYS}.fields.replyToName.placeholder`)}/>
                  </Form.Item>
                  <Form.Item
                    className="gt-mail-field"
                    name="envelopeFrom"
                    label={t(`${KEYS}.fields.envelopeFrom.label`)}
                    tooltip={t(`${KEYS}.fields.envelopeFrom.tooltip`)}
                  >
                    <Input placeholder={t(`${KEYS}.fields.envelopeFrom.placeholder`)}/>
                  </Form.Item>
                </div>
              </div>
            </Card>

            <Card className="gt-mail-section" title={sectionHead('connection')}>
              <div className="gt-mail-section-body">
                <div className="gt-mail-rows">
                  <Form.Item
                    className="gt-mail-field"
                    name="host"
                    label={t(`${KEYS}.fields.host.label`)}
                    rules={[{required: true, message: t(`${KEYS}.messages.required`)}]}
                  >
                    <Input placeholder={t(`${KEYS}.fields.host.placeholder`)}/>
                  </Form.Item>

                  <Form.Item
                    className="gt-mail-field"
                    label={t(`${KEYS}.fields.port.label`)}
                    htmlFor="port"
                  >
                    <div className="gt-mail-inline">
                      <Form.Item name="port" noStyle>
                        <Input
                          className="gt-mail-port"
                          placeholder={t(`${KEYS}.fields.port.placeholder`)}
                        />
                      </Form.Item>
                      {!values.port.trim() && (
                        <span className="gt-mail-hint">
                          {t(`${KEYS}.fields.port.suggested`).replace(
                            '{port}',
                            suggestedPort,
                          )}
                        </span>
                      )}
                    </div>
                  </Form.Item>

                  <Form.Item
                    className="gt-mail-field"
                    label={t(`${KEYS}.fields.encryption.label`)}
                  >
                    <div
                      className="gt-mail-checks"
                      role="group"
                      aria-label={t(`${KEYS}.fields.encryption.label`)}
                    >
                      <Form.Item name="ssl" valuePropName="checked" noStyle>
                        <Checkbox>{t(`${KEYS}.fields.encryption.ssl`)}</Checkbox>
                      </Form.Item>
                      <Form.Item name="starttls" valuePropName="checked" noStyle>
                        <Checkbox>{t(`${KEYS}.fields.encryption.starttls`)}</Checkbox>
                      </Form.Item>
                    </div>
                  </Form.Item>

                  <Form.Item
                    className="gt-mail-field"
                    label={t(`${KEYS}.fields.auth.label`)}
                  >
                    <div className="gt-mail-inline">
                      <Form.Item name="auth" valuePropName="checked" noStyle>
                        <Switch aria-label={t(`${KEYS}.fields.auth.label`)}/>
                      </Form.Item>
                      <span className="gt-mail-switch-label">
                        {t(`${KEYS}.fields.auth.${values.auth ? 'enabled' : 'disabled'}`)}
                      </span>
                    </div>
                  </Form.Item>

                  {values.auth && (
                    <div className="gt-mail-auth-block">
                      <div className="gt-mail-rows">
                        <Form.Item
                          className="gt-mail-field"
                          name="username"
                          label={t(`${KEYS}.fields.username.label`)}
                        >
                          <Input
                            placeholder={t(`${KEYS}.fields.username.placeholder`)}
                            autoComplete="off"
                          />
                        </Form.Item>
                        <Form.Item
                          className="gt-mail-field"
                          name="password"
                          label={t(`${KEYS}.fields.password.label`)}
                        >
                          <Input.Password
                            placeholder={t(
                              `${KEYS}.fields.password.${passwordSet ? 'storedPlaceholder' : 'placeholder'}`,
                            )}
                            autoComplete="new-password"
                          />
                        </Form.Item>
                      </div>
                    </div>
                  )}

                  <div className="gt-mail-row-offset gt-mail-inline">
                    <Form.Item name="debug" valuePropName="checked" noStyle>
                      <Checkbox>{t(`${KEYS}.fields.debug.label`)}</Checkbox>
                    </Form.Item>
                    <span className="gt-mail-hint">
                      {t(`${KEYS}.fields.debug.tooltip`)}
                    </span>
                  </div>

                  <Alert
                    type="info"
                    showIcon
                    title={
                      <>
                        {testInfoBefore}
                        {testInfoAfter !== undefined && (
                          <span className="gt-mail-mono">{userEmail}</span>
                        )}
                        {testInfoAfter}
                      </>
                    }
                  />

                  {testing && (
                    <div className="gt-mail-testing">
                      <Spin size="small"/>
                      <span>{t(`${KEYS}.testing`).replace('{host}', hostLabel)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="gt-mail-footer">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  disabled={!dirty || !valid}
                >
                  {t(`${KEYS}.buttons.save`)}
                </Button>
                <Button onClick={handleTest} disabled={!valid || testing}>
                  {t(`${KEYS}.buttons.test`)}
                </Button>
                <Button type="link" onClick={handleRevert} disabled={!dirty}>
                  {t(`${KEYS}.buttons.revert`)}
                </Button>
                <span className="gt-mail-dirty">
                  {t(`${KEYS}.dirty.${dirty ? 'unsaved' : 'saved'}`)}
                </span>
              </div>
            </Card>
          </Form>
        </Spin>
      </div>
    </PageContainer>
  );
};

export default EmailServer;
